import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, merge, take } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  BLOG_QUERY,
  BlogPostDetail,
  BlogPostSummary,
  postAlternates,
  postPhoto,
} from '../seo/blog.config';
import { PublicPriceList, trialDaysOf } from '../seo/price-list.config';
import {
  BLOG_FAQ,
  Lang,
  ORG,
  PRICING_FAQ_COUNT,
  PAGES,
  PageKey,
  SECTORS,
  canonicalPath,
  indexableLangs,
  toLang,
} from '../seo/seo.config';

/** Head elements this service owns, so stale ones are removed between navigations. */
const MANAGED_ATTR = 'data-seo';

/**
 * An article's photo as an absolute URL, for `og:image` and the JSON-LD: the CMS sends an
 * https URL or (in principle) a path of this site — both already filtered by
 * blog-upstream.ts.
 */
function absoluteImage(origin: string, src: string | undefined): string | undefined {
  if (!src) return undefined;
  return src.startsWith('/') ? origin + src : src;
}

/** The seven channels, in the order the home page names them in its first screen. */
const CHANNEL_KEYS = [
  'whatsapp',
  'messenger',
  'instagram',
  'telegram',
  'email',
  'website',
  'phone',
];

/**
 * Per-route SEO head management: title, description, robots, canonical,
 * hreflang alternates, Open Graph / Twitter cards and the JSON-LD graph.
 *
 * Everything is written into the DOCUMENT head, so it is part of the **server
 * rendered** HTML (crawlers never execute the hydration bundle before deciding
 * what a page is about). The page → URL map lives in `seo/seo.config.ts` and is
 * shared with sitemap.xml.
 *
 * Non-indexable builds (`environment.site.indexable === false`) always emit `noindex`:
 * `noindex, follow` where crawlers may still read the site (staging), `noindex, nofollow` in
 * local development.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  private started = false;

  constructor(
    @Inject(DOCUMENT) private readonly doc: Document,
    private readonly title: Title,
    private readonly meta: Meta,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly translate: TranslateService,
  ) {}

  /** Subscribe to navigation + language changes. Called once from the root component. */
  init(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    merge(
      this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)),
      this.translate.onLangChange,
    ).subscribe(() => this.update());
    this.update();
  }

  /** Resolve the active page, wait for its translations, then write the head. */
  private update(): void {
    const resolved = this.activePage();
    if (!resolved) {
      return;
    }
    // `get()` emits once the language file is loaded — after that `instant()` is safe
    // for every other key (including the FAQ block used by the JSON-LD graph).
    this.translate
      .get('seo.siteName')
      .pipe(take(1))
      .subscribe(() => this.apply(resolved.key, resolved.lang, resolved.params));
  }

  /**
   * Walks the activated route tree collecting `data.seo` and the merged params.
   * Params are collected explicitly because the router's default inheritance
   * strategy does not push `:lang` down to non-empty child paths.
   *
   * Public because the language switcher needs the same answer to jump to the
   * *canonical* URL of the other language (`/it/funzioni` → `/en/features`)
   * instead of only swapping the language segment.
   */
  activePage(): { key: PageKey; lang: Lang; params: Record<string, string> } | null {
    const params: Record<string, string> = {};
    let key: PageKey | undefined;
    let node: ActivatedRouteSnapshot | null = this.route.snapshot.root;
    while (node) {
      for (const name of node.paramMap.keys) {
        const value = node.paramMap.get(name);
        if (value !== null) {
          params[name] = value;
        }
      }
      if (node.data['seo']) {
        key = node.data['seo'] as PageKey;
      }
      node = node.firstChild;
    }
    return key ? { key, lang: toLang(params['lang']), params } : null;
  }

  private apply(route: PageKey, lang: Lang, params: Record<string, string>): void {
    // A sector URL whose id names no sheet renders the 404 page (see feature.main): its head
    // is the 404's too, not a title built around a name that does not exist.
    const key: PageKey =
      route === 'useCaseDetail' && !SECTORS.some((s) => s.id === params['id']) ? 'notFound' : route;
    const page = PAGES[key];
    const t = (k: string, p?: object): string => this.translate.instant(k, p);
    const origin = environment.site.url;

    // The article resolved for this route (see blog.resolvers.ts). A blog URL whose slug
    // matched no published article is a dead URL, not a page: keep it out of the index
    // rather than letting it serve a generic title.
    const post = key === 'blogPost' ? this.resolvedPost() : null;
    // A page not yet translated into THIS language is served (the fallback language fills
    // the text) but never advertised: Italian copy sitting under `/en` must not be indexed
    // as the English version of the page.
    const indexable =
      environment.site.indexable &&
      !page.noindex &&
      !page.noindexLangs?.includes(lang) &&
      !(key === 'blogPost' && !post);

    // The detail page borrows the sector's own name so each of the six URLs gets a
    // distinct, keyword-bearing title instead of one shared boilerplate. The SHORT
    // `nav` label is used, not the full `title`, to keep every variant of the
    // templated title under the ~60 characters Google renders.
    const sector = key === 'useCaseDetail' ? t(`useCases.${params['id']}.nav`) : '';
    // The trial's days, from the price list the page resolved (Prices, checkout): the copy that
    // names them (`{{days}}`) reads them here, as the page itself does.
    const days = trialDaysOf(this.deepestData()['priceList'] as PublicPriceList | null | undefined);
    const i18nParams = sector ? { sector, days } : { days };

    // Articles carry their own title and description, written per post. The CMS titles
    // end in "| Chattoo": the tab keeps the site-wide "Topic · Chattoo" form.
    const title = post
      ? `${(post.seoTitle || post.title).replace(/\s*[|·–-]\s*Chattoo\s*$/i, '')} · ${ORG.name}`
      : t(`seo.${page.i18n}.title`, i18nParams);
    const description = post
      ? post.seoDescription || post.excerpt || t(`seo.${page.i18n}.description`)
      : t(`seo.${page.i18n}.description`, i18nParams);
    const canonical = origin + canonicalPath(key, lang, params);
    // The page's share preview, the site's as the fallback. An article with a cover in the
    // CMS is shared with its own photo (`og:image`, `twitter:image`, JSON-LD).
    const siteImage = origin + (page.ogImage ?? ORG.ogImage);
    const postImage = absoluteImage(origin, post?.image);
    const image = postImage ?? siteImage;

    // `lang` on <html> is set here (not only in LanguageService) so it is correct in the
    // server-rendered HTML too — index.html ships a hardcoded `lang="it"`.
    this.doc.documentElement.lang = lang;

    this.title.setTitle(title);
    this.setName('description', description);
    // A tier that is crawled but not indexed (staging) keeps its links followed: the crawlers
    // that read it — the console's assistant setup among them — still walk the whole site.
    const tierCrawledOnly = !environment.site.indexable && environment.site.crawlable;
    this.setName(
      'robots',
      indexable
        ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
        : tierCrawledOnly
          ? 'noindex, follow'
          : 'noindex, nofollow',
    );

    // ---- Open Graph / Twitter ------------------------------------------------
    this.setProperty('og:type', post ? 'article' : 'website');
    this.setProperty('og:site_name', ORG.name);
    this.setProperty('og:title', title);
    this.setProperty('og:description', description);
    this.setProperty('og:url', canonical);
    this.setProperty('og:image', image);
    if (post && postImage) {
      // The CMS does not say how big a cover is: no size rather than the site image's.
      this.meta.removeTag("property='og:image:width'");
      this.meta.removeTag("property='og:image:height'");
      this.setProperty('og:image:alt', post.imageAlt || post.title);
    } else {
      this.setProperty('og:image:width', '1200');
      this.setProperty('og:image:height', '630');
      this.setProperty('og:image:alt', t('seo.ogImageAlt'));
    }
    this.setProperty('og:locale', lang === 'it' ? 'it_IT' : 'en_GB');
    this.setProperty('og:locale:alternate', lang === 'it' ? 'en_GB' : 'it_IT');
    this.setName('twitter:card', 'summary_large_image');
    this.setName('twitter:title', title);
    this.setName('twitter:description', description);
    this.setName('twitter:image', image);

    // ---- canonical + hreflang ------------------------------------------------
    // Both slugs of every page resolve to the same component, so `/it/features`
    // and `/it/funzioni` are the same document: the canonical collapses them onto
    // one URL per language, and the reciprocal hreflang pair links the two.
    this.clearManaged('link');
    this.addLink({ rel: 'canonical', href: canonical });
    // Il feed RSS si annuncia dove ha senso seguirlo: indice del blog e articoli.
    if (key === 'blog' || key === 'blogPost') {
      this.addLink({
        rel: 'alternate',
        type: 'application/rss+xml',
        title: t('seo.blog.title'),
        href: `${origin}/${lang}/rss.xml`,
      });
    }
    // The photo at the top of an article, or of the featured article in the index, is the
    // page's largest element (LCP): announced here it starts downloading with the document
    // instead of after the CSS and the code (Lighthouse: LCP 3.1–3.3 s on a phone).
    this.preloadPostPhoto(key, post);
    if (indexable) {
      // An article advertises exactly the languages it is PUBLISHED in, taken from the
      // group's `alternates`; every other page has all languages by construction.
      const pairs = post
        ? postAlternates(post).map((a) => ({
            lang: a.lang,
            href: origin + canonicalPath(key, a.lang, { slug: a.slug }),
          }))
        : indexableLangs(key).map((alt) => ({
            lang: alt,
            href: origin + canonicalPath(key, alt, params),
          }));
      for (const pair of pairs) {
        this.addLink({ rel: 'alternate', hreflang: pair.lang, href: pair.href });
      }
      const italian = pairs.find((p) => p.lang === 'it') ?? pairs[0];
      if (italian) {
        this.addLink({ rel: 'alternate', hreflang: 'x-default', href: italian.href });
      }
    }

    // ---- structured data -----------------------------------------------------
    this.setJsonLd(
      this.buildGraph({ key, lang, params, post, title, description, canonical, image, siteImage, origin, t, days }),
    );
  }

  /** Schema.org @graph: Organization + WebSite are global, the rest is per page. */
  private buildGraph(ctx: {
    key: PageKey;
    lang: Lang;
    params: Record<string, string>;
    post: BlogPostDetail | null;
    title: string;
    description: string;
    canonical: string;
    /** The page's own image: an article's cover, else the site's. */
    image: string;
    /** The site's share image, for the Organization node. */
    siteImage: string;
    origin: string;
    t: (k: string, p?: object) => string;
    /** The trial's days from the resolved price list (the pricing FAQ names them). */
    days: number;
  }): object {
    const { key, lang, params, post, title, description, canonical, image, siteImage, origin, t, days } = ctx;
    const page = PAGES[key];
    const orgId = `${origin}/#organization`;
    const siteId = `${origin}/#website`;
    const inLanguage = lang === 'it' ? 'it-IT' : 'en-GB';

    // The node describing THIS page. Held in a variable because the blog branches
    // below enrich it (dates, author, @id) instead of adding a second node.
    const webpage: Record<string, unknown> = {
      '@type': page.schemaType ?? 'WebPage',
      '@id': `${canonical}#webpage`,
      url: canonical,
      name: title,
      description,
      isPartOf: { '@id': siteId },
      about: { '@id': orgId },
      primaryImageOfPage: { '@type': 'ImageObject', url: image },
      inLanguage,
    };

    const graph: object[] = [
      {
        '@type': 'Organization',
        '@id': orgId,
        name: ORG.name,
        legalName: ORG.legalName,
        url: `${origin}/`,
        email: ORG.email,
        logo: { '@type': 'ImageObject', url: origin + ORG.logo, width: 3162, height: 847 },
        image: siteImage,
        description: t('seo.organizationDescription'),
        areaServed: ORG.country,
        sameAs: [...ORG.sameAs],
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'sales',
          email: ORG.email,
          availableLanguage: ['it', 'en'],
        },
      },
      {
        '@type': 'WebSite',
        '@id': siteId,
        url: `${origin}/`,
        name: ORG.name,
        description: t('seo.home.description'),
        publisher: { '@id': orgId },
        inLanguage,
      },
      webpage,
    ];

    // Breadcrumbs on every page except the language home.
    if (key !== 'home') {
      const trail: { name: string; url: string }[] = [
        { name: t('nav.home'), url: origin + canonicalPath('home', lang) },
      ];
      if (key === 'useCaseDetail' || key === 'useCaseBusiness') {
        trail.push({ name: t('nav.useCases'), url: origin + canonicalPath('useCases', lang) });
      }
      if (key === 'blogPost') {
        trail.push({ name: t('nav.blog'), url: origin + canonicalPath('blog', lang) });
      }
      // Short page name, not the keyword-loaded <title>: Google renders these verbatim
      // in the SERP breadcrumb line.
      const leaf =
        key === 'useCaseDetail'
          ? t(`useCases.${params['id']}.nav`)
          : post
            ? post.title
            : page.crumb
              ? t(page.crumb)
              : title;
      trail.push({ name: leaf, url: canonical });
      graph.push({
        '@type': 'BreadcrumbList',
        '@id': `${canonical}#breadcrumb`,
        itemListElement: trail.map((item, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: item.name,
          item: item.url,
        })),
      });
    }

    // The product itself, anchored on the home page. `featureList` is the seven channels:
    // they are what the page is about, and they are on it — the feature grid that used to
    // feed this list has moved to the Funzioni page.
    if (key === 'home') {
      graph.push({
        '@type': 'SoftwareApplication',
        '@id': `${origin}/#software`,
        name: ORG.name,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web, iOS, Android',
        url: `${origin}/`,
        description,
        image,
        inLanguage,
        publisher: { '@id': orgId },
        featureList: CHANNEL_KEYS.map((id) => t(`home.channels.${id}.name`)),
      });
    }

    // The FAQ lives on the pricing page, and so does its markup — same questions, same
    // wording, both on the page and in the graph.
    if (key === 'pricing') {
      graph.push({
        '@type': 'FAQPage',
        '@id': `${canonical}#faq`,
        mainEntity: Array.from({ length: PRICING_FAQ_COUNT }, (_, i) => ({
          '@type': 'Question',
          name: t(`pricing.faq.q${i + 1}.q`),
          acceptedAnswer: { '@type': 'Answer', text: t(`pricing.faq.q${i + 1}.a`, { days }) },
        })),
      });
    }

    if (key === 'useCaseDetail' && params['id']) {
      graph.push({
        '@type': 'Article',
        '@id': `${canonical}#article`,
        headline: title,
        description,
        image,
        inLanguage,
        isPartOf: { '@id': `${canonical}#webpage` },
        publisher: { '@id': orgId },
        author: { '@id': orgId },
      });
    }

    // A blog article: the page node is already typed BlogPosting, so the dates and
    // the link back to the Blog go on it rather than on a duplicate Article node.
    if (post) {
      // `headline` is the article title — not the SEO title with its brand suffix.
      webpage['headline'] = post.title;
      webpage['image'] = image;
      if (post.publishedAt) webpage['datePublished'] = post.publishedAt;
      webpage['dateModified'] = post.updatedAt ?? post.publishedAt;
      webpage['author'] = { '@id': orgId };
      webpage['publisher'] = { '@id': orgId };
      webpage['mainEntityOfPage'] = canonical;
      webpage['isPartOf'] = { '@id': `${origin}${canonicalPath('blog', lang)}#blog` };
    }

    // The index is the Blog node the articles point back to via `isPartOf`.
    if (key === 'blog') {
      webpage['@id'] = `${canonical}#blog`;
      webpage['blogPost'] = this.resolvedPosts().map((p) => ({
        '@type': 'BlogPosting',
        '@id': `${origin}${canonicalPath('blogPost', lang, { slug: p.slug })}#webpage`,
        headline: p.title,
        url: origin + canonicalPath('blogPost', lang, { slug: p.slug }),
        ...(p.image ? { image: absoluteImage(origin, p.image) } : {}),
        datePublished: p.publishedAt,
        dateModified: p.updatedAt ?? p.publishedAt,
        author: { '@id': orgId },
      }));
      // The questions at the bottom of the index get their FAQPage where the page shows them:
      // the same questions, in the same order and wording, and only on the first page.
      if (this.blogFirstPage()) {
        graph.push({
          '@type': 'FAQPage',
          '@id': `${canonical}#faq`,
          mainEntity: BLOG_FAQ.flatMap((count, g) =>
            Array.from({ length: count }, (_, q) => ({
              '@type': 'Question',
              name: t(`blog.faq.g${g + 1}.q${q + 1}.q`),
              acceptedAnswer: { '@type': 'Answer', text: t(`blog.faq.g${g + 1}.q${q + 1}.a`) },
            })),
          ),
        });
      }
    }

    return { '@context': 'https://schema.org', '@graph': graph };
  }

  /** `<link rel="preload">` for the blog photo that will be the LCP of this page, if any. */
  private preloadPostPhoto(key: PageKey, post: BlogPostDetail | null): void {
    let photo: ReturnType<typeof postPhoto>;
    if (key === 'blogPost') {
      photo = postPhoto(post);
    } else if (key === 'blog') {
      // Same rule as BlogIndex: a featured article only on page 1, without a category.
      if (!this.blogFirstPage()) return;
      photo = postPhoto(this.resolvedPosts()[0]);
    } else {
      return;
    }
    if (!photo) return;
    this.addLink({ rel: 'preload', as: 'image', href: photo.src, fetchpriority: 'high' });
  }

  /** Article resolved for the current route, if any. */
  private resolvedPost(): BlogPostDetail | null {
    return this.deepestData()['post'] as BlogPostDetail | null;
  }

  /** Published article list resolved for the current route. */
  private resolvedPosts(): BlogPostSummary[] {
    return (this.deepestData()['posts'] as BlogPostSummary[]) ?? [];
  }

  /**
   * The Blog index on its first page and without a category: the only view with the featured
   * article and the questions at the bottom (see BlogIndex).
   */
  private blogFirstPage(): boolean {
    const q = this.router.parseUrl(this.router.url).queryParams;
    return !q[BLOG_QUERY.category] && (!q[BLOG_QUERY.page] || q[BLOG_QUERY.page] === '1');
  }

  private deepestData(): Record<string, unknown> {
    let node: ActivatedRouteSnapshot | null = this.route.snapshot.root;
    let data: Record<string, unknown> = {};
    while (node) {
      data = { ...data, ...node.data };
      node = node.firstChild;
    }
    return data;
  }

  /* ---------------- head plumbing ---------------- */

  private setName(name: string, content: string): void {
    this.meta.updateTag({ name, content });
  }

  private setProperty(property: string, content: string): void {
    this.meta.updateTag({ property, content });
  }

  private addLink(attrs: Record<string, string>): void {
    const el = this.doc.createElement('link');
    for (const [k, v] of Object.entries(attrs)) {
      el.setAttribute(k, v);
    }
    el.setAttribute(MANAGED_ATTR, '');
    this.doc.head.appendChild(el);
  }

  private setJsonLd(data: object): void {
    this.clearManaged('script');
    const el = this.doc.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute(MANAGED_ATTR, '');
    // JSON.stringify escapes nothing dangerous here (all values come from our own
    // i18n bundle), but `</script>` inside a string would break out of the tag.
    el.textContent = JSON.stringify(data).replace(/</g, '\\u003c');
    this.doc.head.appendChild(el);
  }

  private clearManaged(tag: 'link' | 'script'): void {
    this.doc.head
      .querySelectorAll(`${tag}[${MANAGED_ATTR}]`)
      .forEach((el: Element) => el.remove());
  }
}
